import { useSubscribe, events } from '@fs/zion-tree-pubsub'
import type { Conclusion, CoupleId, RelationshipId, RelationshipType } from '@fs/zion-tree-types'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { updatePersonObject } from '@fs/zion-person-service'
import { usePersonPageParams } from '../person-page/personPageRouteUtil'
import getCoupleIdFromParentIds from '../person-page/components/family-members/getCoupleId'
import { mutateFocusPerson } from './useFocusPerson'
import { mutateFocusFamilyMembers } from './useFocusFamilyMembers'
import { mutateRelationshipData } from './useRelationshipData'
import useOrdinances from './useOrdinances'
import { mutateOtherRelationships } from './useOtherRelationships'
import { mutateChildren } from './useChildren'
import usePersonQualityScore from './usePersonQualityScore'
import { useContributionsContext } from '../providers/ContributionsProvider'

/**
 * Manages subscribing, unsubscribing, and responding to all data pubsub events
 * that are used in PersonPage.
 */
export default function usePubSub(): void {
  const { pid } = usePersonPageParams('full')
  const { mutateOrdinances } = useOrdinances()
  const { mutatePersonQualityScore } = usePersonQualityScore()
  const { currentTreeId } = useGroupsContext()
  const { refetchContributions } = useContributionsContext()

  // data type is defined by tree-pubsub
  function updateDependentData(data: {
    relationshipId: RelationshipId
    relationshipType: RelationshipType
    conclusion?: Conclusion
    coupleId?: CoupleId
  }): void {
    const { relationshipId, relationshipType, conclusion, coupleId } = data
    mutateFocusPerson(pid, currentTreeId || 'PRIVATE')
    mutatePersonQualityScore()
    mutateFocusFamilyMembers(pid, currentTreeId || 'PRIVATE')
    refetchContributions()
    if (relationshipId && relationshipType !== 'association') {
      mutateRelationshipData(relationshipId, relationshipType)
    }
    if (relationshipId && relationshipType === 'association') {
      mutateOtherRelationships(pid, currentTreeId || 'PRIVATE')
    }
    if ((conclusion?.type === 'PARENT1_LINEAGE' || conclusion?.type === 'PARENT2_LINEAGE') && coupleId) {
      mutateChildren(coupleId, pid, currentTreeId || 'PRIVATE')
    }
    // When the person's vitals, or parents change, it could change the temple ordinances rollup status
    if (
      relationshipType === 'parent-child' ||
      conclusion?.type === 'NAME' ||
      conclusion?.type === 'GENDER' ||
      conclusion?.type === 'BIRTH' ||
      conclusion?.type === 'CHRISTENING' ||
      conclusion?.type === 'DEATH' ||
      conclusion?.type === 'BURIAL'
    ) {
      mutateOrdinances()
        .then(() => {
          // Any time we update ordinance-impacting conclusions, we want to update that person's temple rollup status. Despite being a chained promise, a timeout is still needed for updates to propagate appropriately.
          // Currently we don't get a CALCULATING rollup status back from the reservations team after one of these changes is made, so waiting a few seconds is generally long enough that the rollup status will have officially changed.
          // JIRA: https://fhjira.churchofjesuschrist.org/browse/TW-5161
          return setTimeout(() => {
            return mutateFocusPerson(pid, currentTreeId || 'PRIVATE')
          }, 5000)
        })
        .catch((err) => console.warn(err))
    }
    /** OrdinancesReserveDialog uses usePerson with cache and we want to invalidate the cache (2 weeks).
    resolves https://icseng.atlassian.net/browse/RESV-8300 */
    updatePersonObject(pid, 'summary', false, true)
  }
  useSubscribe({ evt: events.conclusion }, updateDependentData)

  function updatePersons(data): void {
    // When a person is added mutate the focus family
    mutateFocusPerson(pid, currentTreeId || 'PRIVATE')
    mutateFocusFamilyMembers(pid, currentTreeId || 'PRIVATE')
    refetchContributions()
    if (data?.operationType === 'addChild') {
      mutateChildren(
        getCoupleIdFromParentIds(data.relationship.parents.parent1Id, data.relationship.parents.parent2Id),
        pid,
        currentTreeId || 'PRIVATE'
      )
    }
    mutatePersonQualityScore()
  }
  useSubscribe({ evt: events.addPerson }, updatePersons)

  function updateOrdinances(data): void {
    if (
      data.reserve?.some((personId) => personId === pid) ||
      data.unreserve?.some((personId) => personId === pid) ||
      data.shareWithGroup?.some((personId) => personId === pid) ||
      data.shareWithTemple?.some((personId) => personId === pid) ||
      data.unshareWithTemple?.some((personId) => personId === pid) ||
      data.unshareWithGroup?.some((personId) => personId === pid) ||
      data.print?.some((personId) => personId === pid) ||
      (data instanceof Array && data.some((obj) => obj?.person?.id === pid))
    ) {
      mutateOrdinances()
        // Any time we update ordinance state, we want to update the corresponding person's temple rollup status. There is no timeout needed here because we are waiting for the actual reservation call to complete, first.
        // JIRA: https://fhjira.churchofjesuschrist.org/browse/TW-5485
        .then(() => {
          return mutateFocusPerson(pid, currentTreeId || 'PRIVATE')
        })
        .catch((err) => console.warn(err))
    }
  }
  useSubscribe({ evt: events.ordinanceReserve }, updateOrdinances)

  function updateSourcesOnConclusions(data: { action: 'edit-source-ref' }): void {
    const { action } = data
    if (action === 'edit-source-ref') mutateFocusPerson(pid, currentTreeId || 'PRIVATE')
    mutatePersonQualityScore()
  }
  // Note this may cause inconstant updates to source list if more than one source list is rendered at the same time.
  useSubscribe({ evt: events.source }, updateSourcesOnConclusions)
}
